export type FileShareNavigator = Pick<Navigator, "canShare" | "share">;

export function canSharePdfFiles(
  navigatorLike: Partial<FileShareNavigator>,
  files: File[],
): boolean {
  if (typeof navigatorLike.share !== "function" || typeof navigatorLike.canShare !== "function") {
    return false;
  }
  try {
    return navigatorLike.canShare({ files });
  } catch {
    return false;
  }
}

export async function sharePoPdf(
  url: string,
  filename: string,
  dependencies: {
    navigatorLike?: Partial<FileShareNavigator>;
    fetcher?: typeof fetch;
    download?: (url: string) => void;
  } = {},
): Promise<"shared" | "downloaded" | "cancelled"> {
  const navigatorLike = dependencies.navigatorLike ?? window.navigator;
  const fetcher = dependencies.fetcher ?? fetch;
  const download = dependencies.download ?? triggerDownload;

  try {
    const response = await fetcher(url, { cache: "no-store" });
    if (!response.ok) throw new Error(`PDF request failed (${response.status})`);
    const file = new File([await response.blob()], filename, { type: "application/pdf" });
    if (!canSharePdfFiles(navigatorLike, [file])) {
      download(url);
      return "downloaded";
    }
    try {
      await navigatorLike.share?.({ files: [file], title: filename.replace(/\.pdf$/i, "") });
      return "shared";
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return "cancelled";
      download(url);
      return "downloaded";
    }
  } catch {
    download(url);
    return "downloaded";
  }
}

function triggerDownload(url: string): void {
  const link = document.createElement("a");
  link.href = url;
  link.download = "";
  document.body.appendChild(link);
  link.click();
  link.remove();
}
